import { jsClientDeviceIdentifier } from 'header-scripts';
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { Loading } from 'react-style-guide';
import PropTypes from 'prop-types';
import gamesService from '../../gameData/services/gamesService';
import gamesApiConstants from '../../gameData/constants/gamesApiConstants';
import CarouselContainer from './CarouselContainer';
import GridContainer from '../../gameGrid/components/GridContainer';
import GameContainerHeader from '../../gameLists/components/GameContainerHeader';
import gamesPageLayout from '../constants/layoutConstants';

function GamesContainer({ sort, pageContext, sortPosition, sortStyle, timeFilters, ...props }) {
  const [gamesData, setGamesData] = useState([]);
  const {
    token,
    displayName,
    name,
    numberOfRows,
    gameSetTargetId,
    contextCountryRegionId,
    timeOptionsAvailable
  } = sort;
  const { params } = gamesApiConstants;
  const initializedApiParams = {
    sortToken: token,
    gameSetTargetId,
    startRows: params.startRows,
    maxRows:
      sortStyle === gamesPageLayout.sortStyle.featureGameTile
        ? gamesPageLayout.maxRowsLargeTiles
        : gamesPageLayout.maxRows,
    hasMoreRows: true,
    sortPosition,
    contextCountryRegionId
  };
  if (timeOptionsAvailable) {
    initializedApiParams.timeFilter = gamesService.getTimeFilterToken(timeFilters);
  }
  initializedApiParams['pageContext.pageId'] = pageContext?.pageId;
  const [sortApiParams, setSortApiParams] = useState(initializedApiParams);

  const [isInitializedLoading, setInitializedLoading] = useState(false);
  const [isApiRequestPending, setApiRequestPending] = useState(false);

  const isLoadingMoreGames = () => {
    if (!isApiRequestPending && sortApiParams.hasMoreRows) {
      // TODO: need to either pick up from settings the max number of games or always return true here
      return gamesData?.length <= gamesPageLayout.thresholdOfMaxNumOfGamesForSort;
    }

    return false;
  };
  const loadGamesList = (isUnsubscribed, isInitialized) => {
    if (isLoadingMoreGames()) {
      if (isInitialized) {
        setInitializedLoading(true);
      }
      setApiRequestPending(true);
      gamesService
        .getGamesList(sortApiParams)
        .then(
          result => {
            if (!isUnsubscribed && result?.games) {
              setGamesData(currentGamesData => {
                const updateGamesData = currentGamesData.concat(result.games);
                return updateGamesData;
              });
              const updatedApiParams = gamesService.buildUpdatedApiParams(result, sortApiParams);
              setSortApiParams(updatedApiParams);
            }
          },
          () => {
            if (!isUnsubscribed) {
              setSortApiParams(currentSortApiParams =>
                Object.assign(currentSortApiParams, { hasMoreRows: false })
              );
            }
          }
        )
        .finally(() => {
          if (!isUnsubscribed && isInitialized) {
            setInitializedLoading(false);
          }

          if (!isUnsubscribed) {
            setApiRequestPending(false);
          }
        });
    }
  };
  useEffect(() => {
    let isUnsubscribed = false;
    loadGamesList(isUnsubscribed, true);
    return () => {
      isUnsubscribed = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seeAllLink = `sortName/${sort.name}`;

  const gamesListContainerClasses = classNames('games-list-container', {
    'is-windows': jsClientDeviceIdentifier?.isWindows
  });

  return (
    <div data-testid='game-carousel-games-container' className={gamesListContainerClasses}>
      <GameContainerHeader headerLabel={displayName} seeAllLink={seeAllLink} {...props} />
      {isInitializedLoading ? <Loading /> : null}
      {gamesData.length > 0 && (
        <div
          className={classNames('horizontal-scroller games-list', {
            'large-tiles': sortStyle === gamesPageLayout.sortStyle.featureGameTile
          })}>
          {numberOfRows === 1 && (
            <CarouselContainer
              gamesData={gamesData}
              name={name}
              loadMore={loadGamesList}
              sortApiParams={sortApiParams}
              pageContext={pageContext}
              sortPosition={sortPosition}
              sortStyle={sortStyle}
              isInitializedLoading={isInitializedLoading}
              {...props}
            />
          )}
          {numberOfRows > 1 && (
            <GridContainer
              gamesData={gamesData}
              pageType={gamesPageLayout.pageType.gameCarousel}
              isNumberRowsLimited
              sort={sort}
              sortPosition={sortPosition}
              pageContext={pageContext}
              {...props}
            />
          )}
        </div>
      )}
    </div>
  );
}

GamesContainer.propTypes = {
  sort: PropTypes.shape({
    token: PropTypes.string,
    displayName: PropTypes.string,
    name: PropTypes.string,
    numberOfRows: PropTypes.number,
    gameSetTargetId: PropTypes.number,
    contextCountryRegionId: PropTypes.number,
    timeOptionsAvailable: PropTypes.bool
  }).isRequired,
  pageContext: PropTypes.shape({ pageId: PropTypes.string }).isRequired,
  sortPosition: PropTypes.number.isRequired,
  sortStyle: PropTypes.string.isRequired,
  timeFilters: PropTypes.arrayOf(
    PropTypes.shape({ token: PropTypes.string, name: PropTypes.string })
  ).isRequired,
  translate: PropTypes.func.isRequired
};

export default GamesContainer;
